import { queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import {
  pcmpActionPlansSchema,
  pcmpRequestCategoriesSchema,
  pcmpRequestCategorySchema,
  type PcmpActionPlan,
  type PcmpRequestCategory,
  type PcmpRequestCategoryCreatePayload,
  type PcmpRequestCategoryUpdatePayload,
} from '../types/pcmpRequestCategory.types';

export const pcmpRequestCategoryQueryKeys = {
  all: () => ['pcmp-request-categories'] as const,
  list: () => [...pcmpRequestCategoryQueryKeys.all(), 'list'] as const,
  actionPlans: () => [...pcmpRequestCategoryQueryKeys.all(), 'action-plans'] as const,
};

/** Every category, active or not, in display order (admin only). */
export const pcmpRequestCategoriesQueryOptions = () =>
  queryOptions({
    queryKey: pcmpRequestCategoryQueryKeys.list(),
    queryFn: async (): Promise<PcmpRequestCategory[]> => {
      const res = await apiClient.get(API.pcmpRequestCategory.list);
      return pcmpRequestCategoriesSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

/** The fixed plan catalog; the API keeps it in code, so it never goes stale. */
export const pcmpActionPlansQueryOptions = () =>
  queryOptions({
    queryKey: pcmpRequestCategoryQueryKeys.actionPlans(),
    queryFn: async (): Promise<PcmpActionPlan[]> => {
      const res = await apiClient.get(API.pcmpRequestCategory.actionPlans);
      return pcmpActionPlansSchema.parse(res.data);
    },
    staleTime: Infinity,
  });

export async function createPcmpRequestCategory(
  payload: PcmpRequestCategoryCreatePayload,
): Promise<PcmpRequestCategory> {
  const res = await apiClient.post(API.pcmpRequestCategory.create, payload);
  return pcmpRequestCategorySchema.parse(res.data);
}

export async function updatePcmpRequestCategory(
  id: number,
  payload: PcmpRequestCategoryUpdatePayload,
): Promise<PcmpRequestCategory> {
  const res = await apiClient.patch(API.pcmpRequestCategory.update(id), payload);
  return pcmpRequestCategorySchema.parse(res.data);
}
